import { useEffect, useState } from 'react'
import type { AdminSectionProps } from './types'
import { db } from '@core/db/db'
import { pluralRuCount } from '@core/utils/plural'
import { ActiveCatalogSection } from './ActiveCatalogSection'
import { CatalogImportSection } from './CatalogImportSection'
import { CatalogVersionsSection } from './CatalogVersionsSection'
import { CategoriesSection } from './CategoriesSection'
import { PosModerationSection } from './PosModerationSection'
import { CatalogDiagnosticsSection } from './CatalogDiagnosticsSection'
import { AdminCard } from './AdminCard'

/** Счётчики для свёрнутых строк окон. */
interface CatalogCounts {
  products: number
  trash: number
  categories: number
  versions: number
  pos: number
}

/**
 * Раздел «Каталог» — тонкий контейнер: только композиция блоков.
 *
 * Порядок окон задан приоритетом работы директора: активный каталог →
 * заявки с касс → импорт → категории → история → диагностика. По умолчанию
 * развёрнуты только «Каталог активной версии» и «Категории».
 *
 * refreshKey синхронизирует историю версий и активный каталог после локального
 * импорта или отката версии.
 */
export function CatalogSection({ core, perms, cashier }: AdminSectionProps) {
  const [refreshKey, setRefreshKey] = useState(0)
  const [counts, setCounts] = useState<CatalogCounts>({
    products: 0,
    trash: 0,
    categories: 0,
    versions: 0,
    pos: 0
  })
  const bump = () => setRefreshKey((k) => k + 1)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [products, trash, categories, versions, pos] = await Promise.all([
        core.catalog.listActiveProducts(),
        core.catalog.listDeletedProducts(),
        core.catalog.listCategoryTree(),
        db.catalogVersions.count(),
        core.catalog.listCreatedOnPosProducts()
      ])
      if (cancelled) return
      setCounts({
        products: products.length,
        trash: trash.length,
        categories: categories.length,
        versions,
        pos: pos.length
      })
    })().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [core.catalog, refreshKey])

  const meta = (text: string) => (
    <span className="text-small font-normal text-muted">{text}</span>
  )

  return (
    <>
      <AdminCard
        id="catalog.active"
        title="Каталог активной версии"
        defaultOpen
        fullHeight
        meta={meta(
          `${pluralRuCount(counts.products, ['товар', 'товара', 'товаров'])} · корзина ${counts.trash}`
        )}
      >
        <ActiveCatalogSection core={core} refreshKey={refreshKey} />
      </AdminCard>

      <AdminCard
        id="catalog.moderation"
        title="Заявки с касс"
        meta={counts.pos > 0 ? meta(`новых: ${counts.pos}`) : undefined}
      >
        <PosModerationSection core={core} perms={perms} cashier={cashier} />
      </AdminCard>

      <AdminCard id="catalog.import" title="Импорт каталога">
        <CatalogImportSection core={core} onImported={bump} />
      </AdminCard>

      <AdminCard
        id="catalog.categories"
        title="Категории"
        defaultOpen
        meta={meta(pluralRuCount(counts.categories, ['категория', 'категории', 'категорий']))}
      >
        <CategoriesSection core={core} refreshKey={refreshKey} />
      </AdminCard>

      <AdminCard
        id="catalog.versions"
        title="История версий каталога"
        meta={meta(pluralRuCount(counts.versions, ['версия', 'версии', 'версий']))}
      >
        <CatalogVersionsSection
          core={core}
          refreshKey={refreshKey}
          onRolledBack={bump}
        />
      </AdminCard>

      {/* Локальная диагностика нужна только администратору. */}
      {cashier?.role === 'admin' && (
        <AdminCard id="catalog.diagnostics" title="Диагностика каталога устройства">
          <CatalogDiagnosticsSection core={core} refreshKey={refreshKey} />
        </AdminCard>
      )}
    </>
  )
}
